<script lang="ts">
  import { Check } from 'lucide-svelte';
  import { Tooltip, TooltipContent, TooltipTrigger } from '$lib/components/ui/tooltip';
  import { copyText } from '$lib/utils/clipboard';

  // ============================================================
  // <CopyableSessionId>  -  Fase 6 helper.
  //
  // Replicable en cualquier lugar donde aparezca un sessionId. Click copia
  // al portapapeles + flash visual "Copied" 1.5s. Tooltip estandar.
  //
  // Variantes:
  //   - size='sm'  (default)  -> text-[11px], padding chico (SessionCard).
  //   - size='md'             -> text-sm, padding mayor (modales).
  // ============================================================

  interface Props {
    sessionId: string;
    size?: 'sm' | 'md';
    /** Si true, evita propagar el click al parent (default true, util en
     * cards/rows clickeables que abrirían otra cosa). */
    stopPropagation?: boolean;
    /** Label semántico para aria-label. Default "Copy session ID".
     * Pasá otro string si reusás el componente para sweep ids u otros. */
    ariaLabel?: string;
  }

  let {
    sessionId,
    size = 'sm',
    stopPropagation = true,
    ariaLabel = 'Copy session ID',
  }: Props = $props();

  let copied = $state(false);

  async function handleClick(e: MouseEvent): Promise<void> {
    if (stopPropagation) e.stopPropagation();
    const ok = await copyText(sessionId);
    if (!ok) return;
    copied = true;
    setTimeout(() => {
      copied = false;
    }, 1500);
  }

  let sizeClass = $derived(
    size === 'md' ? 'px-2 py-1 text-sm' : 'px-1.5 py-0.5 text-[11px]',
  );
</script>

<Tooltip>
  <TooltipTrigger>
    {#snippet child({ props })}
      <button
        {...props}
        type="button"
        class={[
          'hover:bg-accent rounded font-mono tabular-nums transition-colors',
          sizeClass,
          copied && 'bg-success/15 text-success',
        ]
          .filter(Boolean)
          .join(' ')}
        onclick={handleClick}
        aria-label={ariaLabel}
      >
        {#if copied}
          <span class="inline-flex items-center gap-1">
            <Check class="size-3" /> Copied
          </span>
        {:else}
          {sessionId}
        {/if}
      </button>
    {/snippet}
  </TooltipTrigger>
  <TooltipContent>Click to copy</TooltipContent>
</Tooltip>
